import React, { useState, useEffect } from 'react';
import { apiKeysApi } from '../api';
import { Card, PageTitle, Table, Badge, Btn, Input, Modal, Spinner } from '../components/common/UI';
import { Plus, Trash2, RefreshCw, Copy, BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  permissions: { sendMessages: true, viewStatus: true, viewLogs: false },
  rateLimit: { perMinute: 30, perDay: 1000 },
  ipWhitelist: '',
  expiresIn: 30,
};

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [usageData, setUsageData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiKeysApi.list();
      setKeys(data.data);
    } catch (_e) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      const ipList = form.ipWhitelist
        ? form.ipWhitelist.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const body = Object.assign({}, form, { ipWhitelist: ipList });
      const { data } = await apiKeysApi.create(body);
      setNewKey(data.data.fullKey);
      setModal('key');
      setForm(defaultForm);
      await load();
      toast.success('API key created!');
    } catch (err) {
      const d = err.response && err.response.data;
      toast.error((d && d.error) || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    try {
      await apiKeysApi.delete(id);
      toast.success('Deleted');
      await load();
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  const regenerate = async (id) => {
    if (!window.confirm('Regenerate key? Old key will stop working!')) return;
    try {
      const { data } = await apiKeysApi.regenerate(id);
      setNewKey(data.data.fullKey);
      setModal('key');
      toast.success('Key regenerated!');
    } catch (_e) {
      toast.error('Regenerate failed');
    }
  };

  const toggleActive = async (key) => {
    try {
      await apiKeysApi.update(key._id, { isActive: !key.isActive });
      toast.success('Updated');
      await load();
    } catch (_e) {
      toast.error('Update failed');
    }
  };

  const showUsage = async (key) => {
    try {
      const { data } = await apiKeysApi.usage(key._id);
      setUsageData(Object.assign({}, data.data, { name: key.name }));
      setModal('usage');
    } catch (_e) {
      toast.error('Failed to load usage');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const setPermission = (key, val) => {
    setForm((f) => Object.assign({}, f, { permissions: Object.assign({}, f.permissions, { [key]: val }) }));
  };

  const setRateLimit = (key, val) => {
    setForm((f) => Object.assign({}, f, { rateLimit: Object.assign({}, f.rateLimit, { [key]: val }) }));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <PageTitle sub="Manage third-party API access">API Keys</PageTitle>
        <Btn onClick={() => setModal('create')}><Plus size={14} /> Create Key</Btn>
      </div>

      <Card>
        <Table
          loading={loading}
          headers={['Name', 'Prefix', 'Permissions', 'Usage Today', 'Rate Limit', 'Status', 'Expires', 'Actions']}
          rows={keys.map((k) => [
            <span style={{ fontWeight: 600, color: '#e2e8f0' }}>{k.name}</span>,
            <code style={{ fontSize: 11, color: '#64748b' }}>{k.prefix}</code>,
            <div style={{ display: 'flex', gap: 4 }}>
              {k.permissions.sendMessages && <Badge color="green">send</Badge>}
              {k.permissions.viewStatus && <Badge color="blue">status</Badge>}
              {k.permissions.viewLogs && <Badge color="yellow">logs</Badge>}
            </div>,
            <span>{(k.usage && k.usage.today) || 0} / {k.rateLimit && k.rateLimit.perDay}</span>,
            <span style={{ fontSize: 12 }}>{k.rateLimit && k.rateLimit.perMinute}/min</span>,
            <Badge color={k.isActive ? 'green' : 'red'}>{k.isActive ? 'active' : 'inactive'}</Badge>,
            k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never',
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn size="sm" variant="secondary" onClick={() => showUsage(k)}><BarChart2 size={13} /></Btn>
              <Btn size="sm" variant={k.isActive ? 'warning' : 'primary'} onClick={() => toggleActive(k)}>
                {k.isActive ? 'Disable' : 'Enable'}
              </Btn>
              <Btn size="sm" variant="warning" onClick={() => regenerate(k._id)}><RefreshCw size={13} /></Btn>
              <Btn size="sm" variant="danger" onClick={() => del(k._id)}><Trash2 size={13} /></Btn>
            </div>,
          ])}
        />
      </Card>

      {/* Create Modal */}
      <Modal open={modal === 'create'} onClose={() => setModal(null)} title="Create API Key" width={480}>
        <Input
          label="App Name"
          placeholder="My App"
          value={form.name}
          onChange={(e) => setForm(Object.assign({}, form, { name: e.target.value }))}
        />
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#94a3b8', display: 'block', marginBottom: 8 }}>Permissions</label>
          {[['sendMessages', 'Send Messages'], ['viewStatus', 'View Status'], ['viewLogs', 'View Logs']].map((item) => (
            <label key={item[0]} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.permissions[item[0]]}
                onChange={(e) => setPermission(item[0], e.target.checked)}
                style={{ accentColor: '#25d366' }}
              />
              <span style={{ color: '#e2e8f0', fontSize: 13 }}>{item[1]}</span>
            </label>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input
            label="Rate/Minute"
            type="number"
            value={form.rateLimit.perMinute}
            onChange={(e) => setRateLimit('perMinute', parseInt(e.target.value, 10))}
          />
          <Input
            label="Rate/Day"
            type="number"
            value={form.rateLimit.perDay}
            onChange={(e) => setRateLimit('perDay', parseInt(e.target.value, 10))}
          />
        </div>
        <Input
          label="IP Whitelist (comma separated, leave blank for any)"
          placeholder="192.168.1.1, 10.0.0.1"
          value={form.ipWhitelist}
          onChange={(e) => setForm(Object.assign({}, form, { ipWhitelist: e.target.value }))}
        />
        <Input
          label="Expires In Days (0 = never)"
          type="number"
          value={form.expiresIn}
          onChange={(e) => setForm(Object.assign({}, form, { expiresIn: parseInt(e.target.value, 10) }))}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn onClick={create} disabled={saving}>
            {saving ? <Spinner size={14} /> : <Plus size={14} />} Create
          </Btn>
          <Btn variant="secondary" onClick={() => setModal(null)}>Cancel</Btn>
        </div>
      </Modal>

      {/* Show Key Modal */}
      <Modal open={modal === 'key'} onClose={() => { setModal(null); setNewKey(null); }} title="⚠️ Save Your API Key" width={500}>
        <div style={{ background: '#0f172a', border: '1px solid #f59e0b', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <p style={{ color: '#f59e0b', fontSize: 13, marginBottom: 10, fontWeight: 600 }}>⚠️ This key will NOT be shown again!</p>
          <code style={{ color: '#25d366', fontSize: 12, wordBreak: 'break-all', lineHeight: 1.6 }}>{newKey}</code>
        </div>
        <Btn onClick={() => copy(newKey)}><Copy size={14} /> Copy to Clipboard</Btn>
      </Modal>

      {/* Usage Modal */}
      <Modal open={modal === 'usage'} onClose={() => setModal(null)} title={'Usage: ' + (usageData ? usageData.name : '')}>
        {usageData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              ['Today', usageData.usage ? usageData.usage.today : '—'],
              ['Total', usageData.usage ? usageData.usage.total : '—'],
              ['Last Used', usageData.usage && usageData.usage.lastUsed ? new Date(usageData.usage.lastUsed).toLocaleString() : 'Never'],
              ['Remaining Today', usageData.remaining ? usageData.remaining.today : '—'],
              ['Per Minute Limit', usageData.rateLimit ? usageData.rateLimit.perMinute : '—'],
              ['Per Day Limit', usageData.rateLimit ? usageData.rateLimit.perDay : '—'],
            ].map((item) => (
              <div key={item[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #334155' }}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{item[0]}</span>
                <span style={{ color: '#e2e8f0', fontSize: 13 }}>{item[1]}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
